"use client"

import { useState, useContext, useEffect } from "react"
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  InputAdornment,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import "dayjs/locale/es"
import dayjs from "dayjs"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import ClearIcon from "@mui/icons-material/Clear"
import BusinessIcon from "@mui/icons-material/Business"
import PersonIcon from "@mui/icons-material/Person"
import EmailIcon from "@mui/icons-material/Email"
import PhoneIcon from "@mui/icons-material/Phone"
import BadgeIcon from "@mui/icons-material/Badge"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import HomeIcon from "@mui/icons-material/Home"
import ApartmentIcon from "@mui/icons-material/Apartment"
import PublicIcon from "@mui/icons-material/Public"
import MarkunreadMailboxIcon from "@mui/icons-material/MarkunreadMailbox"
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import { ErpContext } from "../../context/ErpContext.js"
import FileUploadDragDrop from "../transaccionComponent/FileUploadDragDrop.js"

// Lista de países hardcoded
const PAISES = [
  "Argentina",
  "Bolivia",
  "Brasil",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Cuba",
  "Ecuador",
  "El Salvador",
  "España",
  "Estados Unidos",
  "Guatemala",
  "Honduras",
  "México",
  "Nicaragua",
  "Panamá",
  "Paraguay",
  "Perú",
  "Puerto Rico",
  "República Dominicana",
  "Uruguay",
  "Venezuela",
]

// Función para detectar el país por defecto basado en la zona horaria
const detectarPaisPorDefecto = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    // Mapeo de zonas horarias a países (principales)
    const timezoneToPais = {
      "America/Argentina": "Argentina",
      "America/La_Paz": "Bolivia",
      "America/Sao_Paulo": "Brasil",
      "America/Santiago": "Chile",
      "America/Bogota": "Colombia",
      "America/Costa_Rica": "Costa Rica",
      "America/Havana": "Cuba",
      "America/Guayaquil": "Ecuador",
      "America/El_Salvador": "El Salvador",
      "Europe/Madrid": "España",
      "America/New_York": "Estados Unidos",
      "America/Los_Angeles": "Estados Unidos",
      "America/Chicago": "Estados Unidos",
      "America/Guatemala": "Guatemala",
      "America/Tegucigalpa": "Honduras",
      "America/Mexico_City": "México",
      "America/Managua": "Nicaragua",
      "America/Panama": "Panamá",
      "America/Asuncion": "Paraguay",
      "America/Lima": "Perú",
      "America/Puerto_Rico": "Puerto Rico",
      "America/Santo_Domingo": "República Dominicana",
      "America/Montevideo": "Uruguay",
      "America/Caracas": "Venezuela",
    }
    
    // Buscar coincidencia en el timezone
    for (const [tz, pais] of Object.entries(timezoneToPais)) {
      if (timezone.includes(tz)) {
        return pais
      }
    }
    
    // Por defecto, retornar Argentina (puedes cambiar esto)
    return "Argentina"
  } catch (error) {
    console.error("Error detectando país:", error)
    return "Argentina"
  }
}

const ModalCrearActualizarEntidad = ({ open, handleClose, entidad, idTipoEntidad }) => {
  const theme = useTheme()
  const { postEntidad, updateEntidad, condicionIva, getCondicionIva, getCondicionIvaByEntidad, isNavbarCollapsed,
  } = useContext(ErpContext)

  function normalizeFechaNacimiento(value) {
    if (!value) return null
    if (dayjs.isDayjs(value)) return value
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed : null
  }

  function normalizeEntidad(data) {
    if (!data) return {}
    return {
      ...data,
      fechaNacimiento: normalizeFechaNacimiento(data.fechaNacimiento),
    }
  }

  // Estados
  const [entidadData, setEntidadData] = useState(() => normalizeEntidad(entidad))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [errors, setErrors] = useState({})
  const [idCondicionIvaEntidad, setIdCondicionIvaEntidad] = useState("")
  const [adjuntos, setAdjuntos] = useState([])
  const [adjuntosExistentes, setAdjuntosExistentes] = useState([])
  const tipoEntidadActual = Number(idTipoEntidad || entidadData?.idTipoEntidad || 0)
  const mostrarAdjuntos = tipoEntidadActual === 1 || tipoEntidadActual === 2

  // Inicializar datos cuando se abre el modal
  useEffect(() => {
    if (open) {
      setEntidadData(normalizeEntidad(entidad))
      setErrors({})
      setError(null)
      
      // Cargar condicionIva si no están cargadas
      if (condicionIva.length === 0) {
        getCondicionIva()
      }

      // Si es edición, cargar la condición IVA actual de la entidad
      if (entidad?.id) {
        loadCondicionIvaForEntidad(entidad.id)
      } else {
        setIdCondicionIvaEntidad("")
      }

      // Cargar adjuntos existentes y resetear nuevos
      if (entidad?.adjuntos) {
        try {
          const parsed = JSON.parse(entidad.adjuntos)
          setAdjuntosExistentes(Array.isArray(parsed) ? parsed : [])
        } catch (e) {
          setAdjuntosExistentes([])
        }
      } else {
        setAdjuntosExistentes([])
      }
      setAdjuntos([])
    }
  }, [open, entidad])

  // Detectar y establecer país por defecto al abrir el modal
  useEffect(() => {
    if (open && !entidadData?.pais) {
      const paisDetectado = detectarPaisPorDefecto()
      setEntidadData(prev => ({ ...prev, pais: paisDetectado }))
    }
  }, [open])

  useEffect(() => {
    if (!mostrarAdjuntos) {
      if (adjuntos.length > 0) setAdjuntos([])
      if (adjuntosExistentes.length > 0) setAdjuntosExistentes([])
    }
  }, [mostrarAdjuntos, adjuntos.length, adjuntosExistentes.length])

  const loadCondicionIvaForEntidad = async (idEntidad) => {
    try {
      const condicionIvaData = await getCondicionIvaByEntidad(idEntidad)
      if (condicionIvaData && condicionIvaData.idCondicionIva) {
        setIdCondicionIvaEntidad(condicionIvaData.idCondicionIva)
      } else {
        setIdCondicionIvaEntidad("")
      }
    } catch (error) {
      console.error("Error al cargar condición IVA:", error)
      setIdCondicionIvaEntidad("")
    }
  }

  // Manejar cambios en los campos
  const handleChange = (e) => {
    const { name, value } = e.target
    setEntidadData({ ...entidadData, [name]: value })

    // Limpiar error del campo cuando se modifica
    if (errors[name]) {
      setErrors({ ...errors, [name]: null })
    }
  }

  // Validar formulario
  const validateForm = () => {
    const newErrors = {}

    // Validar campos requeridos
    if (!entidadData.descripcion?.trim()) {
      newErrors.descripcion = "El nombre es requerido"
    }

    if (entidadData.email && !/\S+@\S+\.\S+/.test(entidadData.email)) {
      newErrors.email = "Email inválido"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Guardar cambios
  const handleSave = async () => {
    if (!validateForm()) return

    setLoading(true)
    setError(null)

    // esTutor: true si algún campo del tutor tiene valor (para compatibilidad con backend)
    const esTutorParaEnviar = !!(entidadData.nombreTutor?.trim() || entidadData.apellidoTutor?.trim() || entidadData.telefonoTutor?.trim())

    try {
      if (entidadData.id) {
        // Si la entidad ya tiene ID -> ACTUALIZAR
        await updateEntidad(
          entidadData.id,
          entidadData.descripcion,
          entidadData.apellido,
          entidadData.telefono,
          entidadData.email,
          entidadData.dniCuitCuil,
          entidadData.direccion,
          entidadData.localidad,
          entidadData.provincia,
          idTipoEntidad,
          idCondicionIvaEntidad,
          // NUEVOS CAMPOS
          entidadData.sexo,
          entidadData.fechaNacimiento,
          entidadData.pais,
          entidadData.codigoPostal,
          entidadData.observaciones,
          esTutorParaEnviar,
          entidadData.nombreTutor,
          entidadData.apellidoTutor,
          entidadData.telefonoTutor,
          // DATOS BANCARIOS
          entidadData.nombreBanco,
          entidadData.numeroCuenta,
          entidadData.cbuCvuCuenta,
          entidadData.aliasCuenta,
          entidadData.esVendedor || false,
          [...adjuntosExistentes, ...adjuntos],
        )
      } else {
        // Si la entidad NO tiene ID -> CREAR
        await postEntidad(
          entidadData.descripcion,
          entidadData.apellido,
          entidadData.telefono,
          entidadData.email,
          entidadData.dniCuitCuil,
          entidadData.direccion,
          entidadData.localidad,
          entidadData.provincia,
          idTipoEntidad,
          idCondicionIvaEntidad,
          // NUEVOS CAMPOS
          entidadData.sexo,
          entidadData.fechaNacimiento,
          entidadData.pais,
          entidadData.codigoPostal,
          entidadData.observaciones,
          esTutorParaEnviar,
          entidadData.nombreTutor,
          entidadData.apellidoTutor,
          entidadData.telefonoTutor,
          // DATOS BANCARIOS
          entidadData.nombreBanco,
          entidadData.numeroCuenta,
          entidadData.cbuCvuCuenta,
          entidadData.aliasCuenta,
          entidadData.esVendedor || false,
          null,             // extraOptions
          adjuntos,
        )
      }

      handleClose(true) // Cierra el modal y actualiza la tabla
    } catch (err) {
      console.error("Error al guardar proveedor:", err)
      if (err?.response?.status === 409) {
        const mensajeDuplicado = "Ya existe un cliente activo con ese DNI/CUIT/CUIL."
        setErrors((prev) => ({ ...prev, dniCuitCuil: mensajeDuplicado }))
        setError(mensajeDuplicado)
      } else {
        setError("Ocurrió un error al guardar los datos. Intente nuevamente.")
      }
    } finally {
      setLoading(false)
    }
  }

  // Limpiar todos los campos
  const handleClear = () => {
    const paisDetectado = detectarPaisPorDefecto()
    setEntidadData({
      id: entidadData.id || null, // Mantener el ID si es edición
      descripcion: "",
      apellido: "",
      dniCuitCuil: "",
      telefono: "",
      email: "",
      direccion: "",
      localidad: "",
      provincia: "",
      idTipoEntidad: idTipoEntidad,
      sexo: "",
      fechaNacimiento: null,
      pais: paisDetectado,
      codigoPostal: "",
      observaciones: "",
      esTutor: false,
      nombreTutor: "",
      apellidoTutor: "",
      telefonoTutor: "",
      // DATOS BANCARIOS
      nombreBanco: "",
      numeroCuenta: "",
      cbuCvuCuenta: "",
      aliasCuenta: "",
    })
    setIdCondicionIvaEntidad("")
    setErrors({})
    setError(null)
  }

  return (
    <Modal open={open} onClose={() => !loading && handleClose(false)} closeAfterTransition>
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 600, md: 700 },
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* Encabezado */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              pl: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
  <BusinessIcon
    sx={{
      fontSize: 24,
      color: 'rgb(244, 147, 14)',
      mr: 1.5,
    }}
  />
  <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
    {`${entidadData.id ? "Editar" : "Crear Nuevo"} ${
      idTipoEntidad === 1
        ? "Cliente"
        : idTipoEntidad === 2
        ? "Proveedor"
        : "Entidad"
    }`}
  </Typography>
</Box>

            <IconButton
              onClick={() => handleClose(false)}
              disabled={loading}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": {
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Contenido del formulario */}
          <Box
            sx={{
              p: 3,
              maxHeight: "calc(90vh - 130px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.text.primary, 0.2),
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: alpha(theme.palette.text.primary, 0.05),
              },
            }}
          >
            {/* Mensaje de error */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Sección: Información Personal */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 0.5,
                    mt: 0,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <PersonIcon sx={{ color: 'rgb(244, 147, 14)',mr: 1 }} />
                  Información Personal
                </Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre / Razón Social"
                  name="descripcion"
                  value={entidadData.descripcion || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.descripcion}
                  helperText={errors.descripcion}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Apellido"
                  name="apellido"
                  value={entidadData.apellido || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="DNI / CUIT / CUIL"
                  name="dniCuitCuil"
                  value={entidadData.dniCuitCuil || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                >
                  <InputLabel>Condición IVA</InputLabel>
                  <Select
                    value={idCondicionIvaEntidad}
                    onChange={(e) => setIdCondicionIvaEntidad(e.target.value)}
                    label="Condición IVA"
                    size="small"
                  >
                    <MenuItem value="">
                      <em>Sin seleccionar</em>
                    </MenuItem>
                    {condicionIva.map((condicion) => (
                      <MenuItem key={condicion.id} value={condicion.id}>
                        {condicion.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {idTipoEntidad === 1 && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                      <InputLabel>Sexo</InputLabel>
                      <Select
                        name="sexo"
                        value={entidadData.sexo || ""}
                        onChange={handleChange}
                        label="Sexo"
                        size="small"
                      >
                        <MenuItem value="">
                          <em>Sin especificar</em>
                        </MenuItem>
                        <MenuItem value="Masculino">Masculino</MenuItem>
                        <MenuItem value="Femenino">Femenino</MenuItem>
                        <MenuItem value="X">X</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                      <DatePicker
                        label="Fecha de Nacimiento"
                        value={normalizeFechaNacimiento(entidadData?.fechaNacimiento)}
                        onChange={(newValue) => setEntidadData({ ...entidadData, fechaNacimiento: newValue })}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            sx: { "& .MuiOutlinedInput-root": { borderRadius: 2 } },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </>
              )}

              {/* Sección: Información de Contacto */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 0.5,
                    mt: 0,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <PhoneIcon sx={{ mr: 1, color: 'rgb(244, 147, 14)' }} />
                  Información de Contacto
                </Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Teléfono"
                  name="telefono"
                  value={entidadData.telefono || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={entidadData.email || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Sección: Dirección */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 0.5,
                    mt: 0,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <LocationOnIcon  sx={{ mr: 1, color: 'rgb(244, 147, 14)' }} />
                  Dirección
                </Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Dirección"
                  name="direccion"
                  value={entidadData.direccion || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Localidad"
                  name="localidad"
                  value={entidadData.localidad || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Provincia"
                  name="provincia"
                  value={entidadData.provincia || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ApartmentIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={PAISES}
                  value={entidadData.pais || null}
                  onChange={(event, newValue) => {
                    setEntidadData({ ...entidadData, pais: newValue || "" })
                  }}
                  freeSolo
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="País"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <PublicIcon color="action" sx={{ ml: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Código Postal"
                  name="codigoPostal"
                  value={entidadData.codigoPostal || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MarkunreadMailboxIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Observaciones - Solo para Clientes */}
              {idTipoEntidad === 1 && (
                <Grid item xs={12}>
                  <TextField
                    label="Observaciones"
                    name="observaciones"
                    value={entidadData.observaciones || ""}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    placeholder="Información adicional: alergias, si es celíaco, restricciones alimentarias, etc."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              )}

              {/* Sección: Datos Bancarios */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 0.5,
                    mt: 0,
                    display: "flex",
                    alignItems: "center",
                    color: '#5b5b5f',
                    fontWeight: 600,
                  }}
                >
                  <AccountBalanceIcon sx={{ mr: 1, color: 'rgb(244, 147, 14)' }} />
                  Datos Bancarios
                </Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre del Banco"
                  name="nombreBanco"
                  value={entidadData.nombreBanco || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalanceIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Número de Cuenta"
                  name="numeroCuenta"
                  value={entidadData.numeroCuenta || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalanceIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="CBU / CVU"
                  name="cbuCvuCuenta"
                  value={entidadData.cbuCvuCuenta || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalanceIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Alias"
                  name="aliasCuenta"
                  value={entidadData.aliasCuenta || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalanceIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Información del Contacto */}
              {idTipoEntidad === 1 && (
                <>
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 0.5,
                        mt: 0,
                        display: "flex",
                        alignItems: "center",
                        color: '#5b5b5f',
                        fontWeight: 600,
                      }}
                    >
                      <PersonIcon sx={{ mr: 1, color: 'rgb(244, 147, 14)' }} />
                      Información del Contacto
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Nombre del Tutor"
                      name="nombreTutor"
                      value={entidadData.nombreTutor || ""}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Apellido del Tutor"
                      name="apellidoTutor"
                      value={entidadData.apellidoTutor || ""}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Teléfono de Contacto del Tutor"
                      name="telefonoTutor"
                      value={entidadData.telefonoTutor || ""}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {/* Sección: Archivos Adjuntos */}
            <>
              {mostrarAdjuntos && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography variant="subtitle1" sx={{
                        mb: 0.5,
                        mt: 0,
                        display: "flex",
                        alignItems: "center",
                        color: '#5b5b5f',
                        fontWeight: 600,
                      }}>
                      <CloudUploadIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
                      Archivos Adjuntos
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                  </Grid>
                  <FileUploadDragDrop
                    files={adjuntos}
                    onFilesChange={setAdjuntos}
                    existingFiles={adjuntosExistentes}
                    onExistingFilesChange={setAdjuntosExistentes}
                    maxFiles={5}
                    maxTotalSizeMB={10}
                  />
                </Grid>
              )}
              </>
          </Box>


          {/* Footer con botones */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          >
            <Button
              variant="outlined"
              onClick={() => handleClose(false)}
              disabled={loading}
              size="small"
              sx={{
                mr: 2,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={loading}
              size="small"
              startIcon={<ClearIcon />}
              sx={{
                mr: 2,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              Limpiar
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              size="small"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                minWidth: 120,
                boxShadow: 2,
              }}
            >
              {loading ? "Guardando..." : entidadData.id ? "Actualizar" : "Crear"}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  )
}

export default ModalCrearActualizarEntidad
